<template>
  <v-container fluid v-if="list">
    <validation-observer ref="form">
      <v-form @submit.prevent="validateForm">
        <v-card outlined class="mb-5">
          <v-container fluid>
            <v-card outlined>
              <v-container fluid>
                <v-row>
                  <v-col cols="12" md="6">
                    <validation-provider
                      class="width-100"
                      rules="required|no_invalid_chars"
                      :name="$t('list.IDList')"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        class="pt-2"
                        v-model="list.list_id"
                        :label="$t('list.IDList')"
                        :error-messages="errors"
                        dense
                        :disabled="!isNewList"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="6">
                    <validation-provider
                      class="width-100"
                      rules="required"
                      :name="$t('list.listName')"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        class="pt-2"
                        v-model="list.name"
                        :label="$t('list.listName')"
                        :error-messages="errors"
                        dense
                        :disabled="loading"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" md="6" v-if="!isDynamic || isNewList">
                    <v-text-field class="pt-2" :value="listTypeLabel" :label="$t('list.listType')" dense disabled />
                  </v-col>
                  <v-col v-if="isDynamic && !isNewList" cols="12">
                    <v-row align="center">
                      <v-col cols="12" md="6">
                        <v-text-field class="mb-4" :value="listTypeLabel" :label="$t('list.listType')" dense disabled />
                        <v-text-field
                          class="mb-4"
                          :value="formatDate(list.last_execution_date)"
                          :label="$t('list.lastExecutionDate')"
                          dense
                          disabled
                        />
                        <v-text-field
                          class="mb-4"
                          :value="formatDate(list.last_value_update)"
                          :label="$t('list.lastValueUpdate')"
                          dense
                          disabled
                        />
                        <div class="d-flex align-center">
                          <span class="text-caption mr-2">{{ $t('list.lastExecutionStatus') }}</span>
                          <span :class="executionStatusColor + '--text'">
                            {{
                              list.last_execution_status
                                ? $t(`list.executionStatus.${list.last_execution_status}`)
                                : '-'
                            }}
                          </span>
                        </div>
                      </v-col>
                      <v-col cols="12" md="6" class="d-flex align-center justify-center justify-md-start">
                        <v-btn
                          color="primary"
                          outlined
                          :disabled="!list.conditions || list.conditions.length === 0 || executing || loading"
                          :loading="executing"
                          @click="executeList()"
                        >
                          <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-container>
            </v-card>

            <template v-if="!isDynamic">
              <v-data-table
                dense
                :headers="headers"
                :items="list.values"
                :loading-text="$t('list.loadingLocations')"
                :no-data-text="$t('list.noLocation')"
                mobile-breakpoint="768"
                item-key="id"
                :footer-props="footerProps"
                :options.sync="options"
                :loading="loading"
                class="mt-5"
              >
                <template v-slot:top>
                  <v-toolbar flat>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      outlined
                      @click="addLocationsModal = true"
                      :loading="loading"
                      :disabled="loading"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                  </v-toolbar>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    class="pl-0 pr-0"
                    color="error"
                    @click="openDeleteLocationDialog(item)"
                    :loading="loading"
                    :disabled="loading"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
              <confirmation-dialog
                v-model="openDeleteLocationModal"
                :text="$t('list.deleteLocation')"
                :title="$t('list.deleteLocationTitle')"
                @no="openDeleteLocationModal = false"
                @yes="deleteLocation(selectedLocation)"
              ></confirmation-dialog>
            </template>

            <v-tabs v-if="isDynamic" v-model="activeTab" class="mt-5">
              <v-tab>{{ $t('list.tabCriteria') }}</v-tab>
              <v-tab>{{ $t('list.tabResults') }}</v-tab>
              <v-tab-item eager>
                <condition-builder
                  v-model="list.conditions"
                  :fields="fields"
                  :has-error="conditionsHasError"
                  class="mt-3"
                />
              </v-tab-item>
              <v-tab-item>
                <v-data-table
                  dense
                  :headers="readOnlyHeaders"
                  :items="list.values || []"
                  :loading-text="$t('list.loadingLocations')"
                  :no-data-text="$t('list.noLocation')"
                  mobile-breakpoint="768"
                  item-key="location_id"
                  :footer-props="footerProps"
                  :options.sync="options"
                  :loading="loading"
                  class="mt-3"
                />
              </v-tab-item>
            </v-tabs>

            <v-toolbar flat justify-content="center" height="auto" class="mb-2 mt-5">
              <v-row justify="center">
                <v-btn
                  v-if="returnContext"
                  class="ma-2"
                  color="primary"
                  outlined
                  :disabled="loading"
                  @click="cancelCreation"
                >
                  {{ $t('list.cancelCreation') }}
                </v-btn>
                <v-btn class="ma-2" color="primary" outlined :loading="loading" :disabled="loading" type="submit">
                  {{ $t('list.save') }}
                </v-btn>
                <v-btn
                  class="ma-2"
                  color="primary"
                  outlined
                  :loading="loading"
                  :disabled="loading"
                  @click="showDeleteListDialog = true"
                >
                  {{ $t('trigger.delete') }}
                </v-btn>
              </v-row>
            </v-toolbar>
          </v-container>
        </v-card>
      </v-form>
    </validation-observer>
    <confirmation-dialog
      v-if="showDeleteListDialog"
      v-model="showDeleteListDialog"
      :text="$t('list.deletionListDialogText')"
      :title="$t('list.deletionListDialogTitle')"
      @no="showDeleteListDialog = false"
      @yes="deleteList()"
    ></confirmation-dialog>
    <selection-dialog
      v-if="!isDynamic"
      v-model="addLocationsModal"
      :title="$t('list.addLocations')"
      :confirm-disabled="!selectedLocations.length"
      @close="addLocationsModal = false"
      @confirm="addLocations"
    >
      <o-select-locations
        v-model="selectedLocations"
        :items="availableLocations"
        :multiple="true"
        :label="$t('settings.locations').toUpperCase()"
        :outlined="true"
        :clearable="true"
        :return-object="true"
      />
    </selection-dialog>
  </v-container>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import confirmationDialog from '@/components/ConfirmationDialog'
  import { displayMessage, getStatusColor } from '@/helpers/helper'
  import selectionDialog from '@/components/dialogs/SelectionDialog.vue'
  import oSelectLocations from '@/components/OSelectLocations.vue'
  import moment from 'moment'
  import { ListService } from '@/services/listService'
  import { LocationService } from '@/services/locationService'
  import ConditionBuilder from '@/components/ConditionBuilder.vue'
  import { readContext, setCreatedListId, clearContext } from '@/helpers/listSelectorContext'

  export default {
    name: 'listDetail',
    data() {
      return {
        locations: [],
        options: {
          itemsPerPage: 15,
        },
        openDeleteLocationModal: false,
        selectedLocation: {},
        selectedLocations: [],
        addLocationsModal: false,
        loading: false,
        executing: false,
        conditionsHasError: false,
        activeTab: 0,
        list: {
          list_id: '',
          name: '',
          type: 'locations',
          mode: 'STATIC',
          values: [],
        },
        fields: [],
        showDeleteListDialog: false,
        isNewList: false,
        listService: {},
        returnContext: null,
      }
    },
    components: {
      ValidationProvider,
      ValidationObserver,
      confirmationDialog,
      selectionDialog,
      oSelectLocations,
      ConditionBuilder,
    },
    async mounted() {
      this.listService = new ListService(this.$auth.accessToken)
      await this.initialize()
    },
    watch: {
      $route() {
        this.initialize()
      },
    },
    beforeRouteLeave(to, from, next) {
      // Abandoning the "Create a list" flow by navigating anywhere other than
      // back to the originating page must drop the stored context, otherwise it
      // survives (up to the TTL) and could leak into a later, unrelated list
      // creation. Returning to `returnTo` (cancel or successful create) is left
      // untouched: that destination consumes and clears the context itself.
      if (this.returnContext && to.fullPath !== this.returnContext.returnTo) {
        clearContext()
      }
      next()
    },
    methods: {
      async initialize() {
        this.activeTab = 0
        this.conditionsHasError = false
        await this.getLocations()
        this.isNewList = this.$route.name === 'listAdd'
        this.returnContext = this.isNewList ? readContext() : null
        if (this.isNewList) {
          const mode = (this.$route.query.mode || 'STATIC').toUpperCase()
          if (mode === 'DYNAMIC') {
            this.list = { list_id: '', name: '', type: 'locations', mode: 'DYNAMIC', conditions: [] }
          } else {
            this.list = { list_id: '', name: '', type: 'locations', mode: 'STATIC', values: [] }
          }
        } else {
          await this.get()
        }
      },
      async validateForm() {
        if (this.isDynamic) {
          if (!this.list.conditions || this.list.conditions.length === 0) {
            this.conditionsHasError = true
            displayMessage(this.$t('list.noConditionError'), 'error')
            return
          }
          for (const block of this.list.conditions) {
            if (block.length === 0) {
              this.conditionsHasError = true
              displayMessage(this.$t('list.emptyConditionBlock'), 'error')
              return
            }
          }
          this.conditionsHasError = false
        }
        if (await this.$refs.form.validate()) {
          if (this.isNewList) {
            await this.create()
          } else {
            await this.update()
          }
        } else {
          displayMessage(this.$t('list.incorrectMandatoryField'), 'error')
        }
      },
      async get() {
        this.loading = true
        const res = await this.listService.get(this.$route.params.id)
        if (res) {
          this.list = res
        } else {
          await this.$router.push('/lists')
        }
        this.loading = false
      },
      async update() {
        this.loading = true
        const res = await this.listService.update(this.$route.params.id, this.list)
        if (res) {
          this.list = res
        }
        this.loading = false
      },
      async create() {
        this.loading = true
        const res = await this.listService.create(this.list)
        if (res) {
          if (this.returnContext) {
            setCreatedListId(res.list_id)
            this.loading = false
            await this.$router.push(this.returnContext.returnTo)
            return
          }
          this.isNewList = false
          await this.$router.push(`/lists/${res.list_id}`)
        }
        this.loading = false
      },
      cancelCreation() {
        this.$router.push(this.returnContext.returnTo)
      },
      async deleteList() {
        this.loading = true
        if (await this.listService.delete(this.$route.params.id)) {
          await this.$router.push({ path: '/lists' })
        }
        this.loading = false
      },
      async getLocations() {
        this.loading = true
        const res = await LocationService.getAllLocations(this.$auth.accessToken)
        if (res) {
          this.locations = res
        }
        this.loading = false
      },
      async executeList() {
        this.executing = true
        const res = await this.listService.execute(this.$route.params.id)
        if (res) {
          this.list = res
        }
        if (this.list.last_execution_status === 'SUCCESS') {
          this.activeTab = 1
        }
        this.executing = false
      },
      deleteLocation() {
        this.list.values.splice(this.list.values.indexOf(this.selectedLocation), 1)
        this.openDeleteLocationModal = false
        this.selectedLocation = {}
      },
      openDeleteLocationDialog(item) {
        this.selectedLocation = item
        this.openDeleteLocationModal = true
      },
      addLocations() {
        this.list.values = [...this.list.values, ...this.selectedLocations]
        this.selectedLocations = []
      },
      formatDate(date) {
        if (!date) return '-'
        const d = moment(date)
        return d.isValid() ? d.format('L LT') : '-'
      },
    },
    computed: {
      isDynamic() {
        return this.list.mode === 'DYNAMIC'
      },
      listTypeLabel() {
        return this.isDynamic ? this.$t('list.modeDynamic') : this.$t('list.modeStatic')
      },
      executionStatusColor() {
        return getStatusColor(this.list.last_execution_status)
      },
      headers() {
        return [
          {
            text: this.$t('list.locationId'),
            value: 'location_id',
          },
          {
            text: this.$t('list.locationName'),
            value: 'name',
          },
          {
            text: '',
            value: 'actions',
            sortable: false,
          },
        ]
      },
      readOnlyHeaders() {
        return [
          {
            text: this.$t('list.locationId'),
            value: 'location_id',
          },
          {
            text: this.$t('list.locationName'),
            value: 'name',
          },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [15, 25, 50],
          'items-per-page-text': this.$t('list.locationsPerPage'),
        }
      },
      availableLocations() {
        if (!this.list?.values) return []
        let alreadySelectedLocations = this.list.values.map((location) => location._id)
        return this.locations.filter((location) => !alreadySelectedLocations.includes(location._id))
      },
    },
  }
</script>
